import styled from '@emotion/styled'
import { Site } from '@pubstack/common/src/site'
import { byName } from '@pubstack/common/src/sort'
import { isStackNameReserved } from '@pubstack/common/src/stack'
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Fonts } from '~/assets/style/fonts'
import Button from '~/components/Button'
import { Flyout } from '~/components/Flyout'
import { RadioButtonGroup, RadioOption } from '~/components/RadioButtonGroup'
import { SelectableOptionsPopover, SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { useInfiniteSites } from '~/api/app/sites.api'
import { useAtomValue } from 'jotai'
import { currentScopeState } from '~/state'
import { useDebouncedValue } from '@mantine/hooks'
import { LoadMoreFooter } from '~/components/LoadMoreFooter'
import { BaseInputElement } from '~/components/Input'
import { PopoverTriggerWrapper } from '~/components/Popover'
import { SelectInput } from '~/components/Select'
import { useStacks } from '~/api/stacks.api'
import { Skeleton } from '~/components/Skeleton'
import { groupBy } from 'lodash-es'
import { Stack } from '@pubstack/common/src/stack'
import { useNavigate } from 'react-router-dom'
import { ViewStackFlyout } from './ViewStackFlyout'

const SubTitle = styled.div`
  padding-bottom: 20px;
`

const DetailsHeader = styled.div`
  display:flex;
  justify-content: space-between;
  align-items:center;
  ${Fonts.H2};
  font-weight: 600;
  padding-bottom: 20px;
`
const OptionsList = styled.div`
  padding: 20px 0;
`

const FilterContainer = styled.div`
  padding: 0.75rem;
  gap: 0.75rem;

  ${PopoverTriggerWrapper} {
    width: 100%;
  }
`

const ITEMS_PER_PAGE = 25
const LOAD_MORE_SITES_TRACKING_LABEL = 'adm-new-stack-load-more-sites-apply'

type StackTemplateFlyoutProps = {
  currentSite: Site
  closeFlyout: () => void
}
export const StackTemplateFlyout: FunctionComponent<StackTemplateFlyoutProps> = ({ currentSite, closeFlyout }) => {
  // Context
  const scope = useAtomValue(currentScopeState)

  // States
  const [siteNameSearch, setSiteNameSearch] = useState<string>('')
  const [isSiteNamesSuggestionOpen, setIsSiteNamesSuggestionOpen] = useState<boolean>(false)
  const [siteFilter, setSiteFilter] = useState<Site | undefined>(currentSite)

  // Hooks
  const [debouncedSiteNameSearch] = useDebouncedValue(siteNameSearch, 300)
  const navigate = useNavigate()

  // API Hooks
  const {
    data: sites,
    isLoading: isLoadingSites,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteSites({
    scopeId: scope.id,
    limit: ITEMS_PER_PAGE,
    filters: {
      // `mediaProduct` as well as the status: a stack recompute writes `admStatus` on every media it touches, so
      // the status alone would offer Node's medias as a source of a One stack template. See `mappingSiteFilters`.
      mediaProduct: ['adm'],
      admStatus: ['to-configure', 'out-of-sync', 'to-deploy', 'up-to-date'],
      search: debouncedSiteNameSearch,
    },
  })

  const { data: stacks, isLoading: isStacksLoading } = useStacks({ siteId: siteFilter?.id, type: 'adm', status: ['live', 'to-deploy'] })

  const { control, handleSubmit, resetField } = useForm<{
    selectedSite: string
    selectedTemplate: string
  }>({ defaultValues: { selectedSite: currentSite.id, selectedTemplate: '' } })
  const [isDetailedViewVisible, setIsDetailedViewVisible] = useState<boolean>(false)
  const sitesOptions: SelectOptionProp[] = (sites?.pages.flatMap((page) => page.data) || []).map((site) => ({ label: site.name, value: site, trackingLabel: 'adm-sites-stack-template-site-apply' }))

  const templatesOptions: RadioOption[] = useMemo(() => {
    const noTemplate: RadioOption = { label: 'No template', value: '', trackingLabel: 'adm-sites-stacks-no-template-set' }
    const optionsFromSelectedSite: RadioOption[] = Object.entries(groupBy(stacks, (stack) => stack.stackGroupId))
      .reduce((acc, [_, stacks]) => {
        acc.push(stacks.sort((a, b) => b.version - a.version)[0])
        return acc
      }, [] as Stack[])
      // The stack with the reserved name first, then the others by name.
      .sort((a, b) => Number(isStackNameReserved(b.name)) - Number(isStackNameReserved(a.name)) || byName(a, b))
      .map((stack) => ({ label: stack.name, value: stack.id, iconName: 'view', trackingLabel: 'adm-sites-stacks-template-set' }))
    return [noTemplate, ...optionsFromSelectedSite]
  }, [stacks])

  const [detailedStack, setDetailedStack] = useState<Stack>()

  const handleLoadMore = useCallback(() => {
    if (isFetchingNextPage) return
    fetchNextPage()
  }, [isFetchingNextPage, fetchNextPage])

  const handleSiteChange = (e: SelectOptionProp) => {
    const site = e.value as Site
    setSiteFilter(site)
    setSiteNameSearch('')
  }

  const onCreateStack = async (template: string) => {
    const templateParam = !template ? '' : `?template=${template}`
    navigate(`/monetization/one/sites/${currentSite.id}/stacks/new${templateParam}`)
    closeFlyout()
  }

  useEffect(() => {
    if (!siteFilter) {
      // Initialises the site filter from the current site.
      // oxlint-disable-next-line react/set-state-in-effect
      setSiteFilter(currentSite)
    } else {
      resetField('selectedTemplate')
    }
  }, [currentSite, siteFilter])

  return (
    <Flyout.Content>
      <Flyout.Title>Use existing stack as template</Flyout.Title>
      <Flyout.Body>
        {isDetailedViewVisible && siteFilter && detailedStack ? (
          <>
            <DetailsHeader>
              <span>{`${detailedStack.name} - v${detailedStack.version} details`}</span>
              <Button variant={'tertiary'} onClick={() => setIsDetailedViewVisible(false)} trackingLabel={'adm-sites-stacks-back'}>
                Back
              </Button>
            </DetailsHeader>
            <ViewStackFlyout site={siteFilter} stack={detailedStack} />
          </>
        ) : (
          <>
            <SubTitle>Select a media then an existing stack to duplicate its settings.</SubTitle>
            {siteFilter?.id === currentSite.id && stacks.length === 0 && !isStacksLoading && (
              <SubTitle>
                This first stack will be used as <strong>Default</strong>.
              </SubTitle>
            )}
            <>
              <SelectableOptionsPopover
                open={isSiteNamesSuggestionOpen}
                options={sitesOptions}
                optionsLoading={isLoadingSites}
                setOpen={setIsSiteNamesSuggestionOpen}
                footerOption={hasNextPage && <LoadMoreFooter loading={isFetchingNextPage} onLoadMore={handleLoadMore} trackingLabel={LOAD_MORE_SITES_TRACKING_LABEL} />}
                filterSection={
                  <FilterContainer>
                    <BaseInputElement
                      variant="small"
                      iconLeft="search"
                      labelIsPlaceholder
                      value={siteNameSearch}
                      onChange={(e) => setSiteNameSearch(e.target.value)}
                      onClear={() => setSiteNameSearch('')}
                    />
                  </FilterContainer>
                }
                trigger={
                  <SelectInput
                    labelIsPlaceholder
                    isOpen={isSiteNamesSuggestionOpen}
                    search={false}
                    label="Media"
                    iconRight="chevron_down"
                    value={siteFilter?.name || ''}
                    onClear={() => setSiteFilter(undefined)}
                  />
                }
                onChange={handleSiteChange}
                maxWidthPopoverSameAsTrigger={true}
              />
              <OptionsList>
                {isStacksLoading ? (
                  <Skeleton />
                ) : (
                  <RadioButtonGroup
                    control={control}
                    name={'selectedTemplate'}
                    options={templatesOptions}
                    onIconClick={async (id) => {
                      const stack = stacks.find((s) => s.id === id)
                      setDetailedStack(stack)
                      setIsDetailedViewVisible(true)
                    }}
                  />
                )}
              </OptionsList>
            </>
          </>
        )}
      </Flyout.Body>
      {!isDetailedViewVisible && (
        <Flyout.Actions>
          <Button variant={'primary'} onClick={handleSubmit((formValues) => onCreateStack(formValues.selectedTemplate))} trackingLabel={'adm-sites-stacks-template-new'}>
            Create stack
          </Button>
        </Flyout.Actions>
      )}
    </Flyout.Content>
  )
}
